import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionCard = ({ subscription, onViewDetails, onCancel, onReactivate, highlight }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300';
      case 'trialing':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
      case 'past_due':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
      case 'canceled':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'CheckCircle2';
      case 'trialing':
        return 'Clock';
      case 'past_due':
        return 'AlertCircle';
      case 'canceled':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  return (
    <div
      className={`bg-card border rounded-lg p-6 hover:shadow-md transition-smooth ${
        highlight
          ? 'border-amber-400 shadow-amber-200/60 shadow-md animate-highlight-fade'
          : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between">
        {/* Customer Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{subscription?.customerName}</h3>
              <p className="text-sm text-muted-foreground">{subscription?.customerEmail}</p>
            </div>
          </div>

          {/* Plan Info */}
          <div className="ml-11 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{subscription?.plan}</span>
              <span className="text-sm text-muted-foreground">•</span>
              <span className="text-sm text-foreground font-semibold">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: subscription?.currency
                })?.format(subscription?.amount)}
              </span>
              <span className="text-sm text-muted-foreground">/ {subscription?.interval}</span>
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={12} />
                <span>Next billing: {new Date(subscription?.nextBillingDate || subscription?.currentPeriodEnd)?.toLocaleDateString()}</span>
              </div>
              {subscription?.trialEnd && (
                <div className="flex items-center gap-1">
                  <Icon name="Clock" size={12} />
                  <span>Trial ends: {new Date(subscription?.trialEnd)?.toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {subscription?.metadata?.formName && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon name="FileText" size={12} />
                <span>{subscription?.metadata?.formName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status & Actions */}
        <div className="flex flex-col items-end gap-3">
          <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${getStatusColor(subscription?.status)}`}>
            <Icon name={getStatusIcon(subscription?.status)} size={12} />
            {subscription?.status?.replace('_', ' ')?.toUpperCase()}
          </span>

          {subscription?.cancelAtPeriodEnd && (
            <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 rounded-md">
              Cancels on {new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}
            </span>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              onClick={() => onViewDetails(subscription)}
            >
              Details
            </Button>
            {(subscription?.status === 'active' || subscription?.status === 'trialing') && !subscription?.cancelAtPeriodEnd && (
              <Button
                variant="outline"
                size="sm"
                iconName="XCircle"
                onClick={() => onCancel(subscription?.id)}
              >
                Cancel
              </Button>
            )}
            {subscription?.cancelAtPeriodEnd && subscription?.status !== 'canceled' && (
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={() => onReactivate(subscription?.id)}
              >
                Reactivate
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;